<template>
  <Authenticated>
    <CogsNav />
    <div class="cogs-dashboard">
      <section class="results-section">
        <div class="site-selector">
          <h2>COGS Analysis & Results</h2>
        </div>
        <div class="results-content" v-if="getCogsSelectedSite">
          <div class="chart">
            <ResultsComponent />
          </div>
          <div class="top-bottom">
            <div class="top-results">
              <p class="blue">Top performers</p>
              <ul>
                <li v-for="best in topPerformingRecipes" :key="best.id" @click="loadItem(best)">
                  <p>{{ best.value.escaped }}</p>
                  <p v-if="best.value.price"><CurrencyFormatter :value="best.value.price" :currency="companySettings.currency" /></p>
                  <p>{{ best.value.percentage }}%</p>
                </li>
              </ul>
            </div>
            <div class="bottom-results">
              <p class="blue">Under performers</p>
              <ul>
                <li v-for="worst in underPerformingRecipes" :key="worst.id" @click="loadItem(worst)">
                  <p>{{ worst.value.escaped }}</p>
                  <p v-if="worst.value.price"><CurrencyFormatter :value="worst.value.price" :currency="companySettings.currency" /></p>
                  <p>{{ worst.value.percentage }}%</p>
                </li>
              </ul>
            </div>
            <div class="attention-required">
              <p class="blue">Attention required</p>
              <ul>
                <li v-for="attention in attentionRequiredRecipes" :key="attention.id" @click="loadItem(attention)">
                  <p>{{ attention.value.escaped }}</p>
                  <p v-if="attention.value.price"><CurrencyFormatter :value="attention.value.price" :currency="companySettings.currency" /></p>
                  <p>No data</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else class="show-selector-message">
          <p>Select a site to view results</p>
          <div class="site-buttons">
            <button v-for="site in sites" :key="site.site" class="action-colour" @click="selectSite(site.site)">
              {{ site.site }}
            </button>
          </div>
        </div>
      </section>

    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "@/components/_layouts/Authenticated";
import ResultsComponent from "./Results.vue";
import { cogsMixin } from "./shared/cogsMixin";
import CogsNav from "./shared/CogsNav.vue";
import { CurrencyFormatter } from "@monolith/components";

export default {
  name: "COGSDashboard",
  components: { Authenticated, ResultsComponent, CogsNav, CurrencyFormatter },
  mixins: [cogsMixin],
  data() {
    return {
      refreshing: false,
    };
  },
  computed: {
    totalRecipes() {
      return this.cogsBaseRecipes.length + this.cogsMenuRecipes.length;
    },

    underPerformingRecipes() {
      return this.cogsMenuRecipes.filter((r) => r.value.percentage && parseFloat(r.value.percentage) >= 30);
    },

    topPerformingRecipes() {
      return this.cogsMenuRecipes.filter((r) => r.value.percentage && parseFloat(r.value.percentage) <= 10);
    },

    attentionRequiredRecipes() {
      let attention = this.cogsMenuRecipes.map((recipe) => {
        if (!recipe.value.percentage) {
          return recipe;
        }
      });
      return attention.filter((r) => r !== undefined);
    },
  },
  watch: {
    getCogsSelectedSite: function () {
      this.calculateMenuRecipePercentages();
    },
  },
  methods: {
    selectSite(site) {
      this.$store.dispatch("updateCogsSelectedSite", site);
    },
    loadItem(item) {
      this.$router.push({ name: "site-menus", params: { recipeId: item.id, loadProduct: true } });
    },
  },
  mounted() {
    if (this.getCogsSelectedSite) this.calculateMenuRecipePercentages();
  },
};
</script>

<style lang="scss" scoped>
.cogs-dashboard {
  padding: 0 1rem;

  .results-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 1.5rem 0;
    background: var(--primary-colour);
    border-radius: 1rem;
    box-shadow: var(--box-shadow);

    .site-selector {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 2rem;

      h2 {
        display: flex;
        flex-direction: column;
        margin-bottom: 1rem;
        color: var(--text-colour);
        small {
          font-size: 0.8rem;
          color: var(--action-colour);
          text-transform: capitalize;
        }
      }

      label {
        margin-bottom: 0.5rem;
        color: var(--action-colour);
        font-size: 1rem;
      }

      select {
        padding: 0.5rem;
        border-radius: 0.5rem;
        border: 1px solid var(--border-colour);
        background-color: var(--secondary-colour);
        color: var(--text-colour);
      }
    }

    .show-selector-message {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 3rem;
      gap: 1.5rem;
      color: var(--action-colour);
      font-size: 1.2rem;

      p { margin: 0; }

      .site-buttons {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        justify-content: center;

        button {
          padding: 0.75rem 2rem;
          font-size: 1rem;
          margin: 0;
        }
      }
    }
    .results-content {
      display: flex;

      .chart {
        flex: 3;
        min-width: 0;
        padding: 1rem;
      }

      .top-bottom {
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 2;
        min-width: 0;
        gap: 0.5rem;

        .top-results,
        .bottom-results,
        .attention-required {
          position: relative;
          border-radius: 0.5rem;
          padding: 0 1rem;

          .blue {
            font-weight: bold;
            color: var(--action-colour);
            text-transform: uppercase;
            font-size: 0.9rem;
            letter-spacing: 0.5px;
          }

          ul {
            height: 10rem;
            overflow: auto;
            list-style: none;
            padding: 0;
            margin: 0;
            gap: 0.5rem;

            li {
              background-color: var(--secondary-colour);
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem;
              margin: 0.25rem;
              border-radius: 0.5rem;
              box-shadow: var(--box-shadow);
              cursor: pointer;

              p {
                margin: 0;
                font-weight: normal;
                text-transform: none;
                letter-spacing: normal;
              }
            }
          }
        }

        .top-results {
          ul li p:last-child {
            color: var(--success-colour);
          }
        }

        .bottom-results {
          ul li p:last-child {
            color: var(--warning-colour);
          }
        }

        .attention-required {
          ul li p:last-child {
            color: var(--action-colour);
          }
        }
      }
    }
  }

}

@media only screen and (orientation: portrait) {
  .cogs-dashboard {
    .overview-stats {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .navigation-cards {
      grid-template-columns: 1fr;
      gap: 1.5rem;

      .nav-card {
        padding: 1.5rem;

        .nav-actions {
          flex-direction: column;
        }
      }
    }
  }
}
</style>
